import { filing } from './toolkit';

/**
 * Form WD-2: petition of an administrator for leave to compromise a wrongful death action,
 * allocate the recovery and have the account judicially settled (NY Surrogate's Court).
 * Every widget is a single-line Text field. Multi-line narratives are one answer per printed line.
 */
export default filing('file-1787852867793-cfcc469f-b438-4167-b069-8fd70195e4cb.pdf', 'Wrongful death petition (WD-2)', f => {
  const p = (n: number, page: number) => `topmostSubform[0].Page${page}[0].TextField${n}[0]`;
  const SUFFIX = 'administratorSuffix';
  const suffix = (n: number, page: number, where: string) => f.answer(p(n, page), SUFFIX, `${where}: Administrat___ ending`);

  // Page 1: caption
  f.shared(p(1, 1), 'estate.surrogateCourtCounty', "Caption: Surrogate's Court, County of");
  f.shared(p(2, 1), 'executor.fullName', 'Caption: In the Matter of the Application of (petitioner, administrator)');
  f.dropdown(p(3, 1), SUFFIX, 'Administrat___ ending printed throughout the petition (or = administrator, rix = administratrix)', 'Caption', ['or', 'rix'],
    'Printed after "Administrat" wherever the form refers to the petitioner as administrator or administratrix.');
  f.shared(p(4, 1), 'decedent.fullName', 'Caption: Goods, Chattels and Credits which were of (decedent), Deceased');
  f.shared(p(5, 1), 'estate.courtFileNumber', 'Caption: File No.');
  suffix(6, 1, 'Caption: settled an account of the proceedings as such Administrat___');

  // Paragraphs 1 to 4
  f.shared(p(7, 1), 'executor.fullName', '1. Petitioner (name)');
  f.shared(p(8, 1), 'executor.relationshipToDecedent', '1. Petitioner is the ___ of the decedent (relationship)');
  f.shared(p(9, 1), 'executor.fullAddress', '1. Petitioner presently resides at');
  f.shared(p(10, 1), 'decedent.addresses[0].city', '2. The decedent died a resident of (city, town or village)');
  f.shared(p(11, 1), 'decedent.addresses[0].county', '2. County of (decedent residence county)');
  f.shared(p(12, 1), 'decedent.dateOfDeath', '2. Date of death');
  f.input(p(13, 1), 'residedWith', '2. The decedent had resided there with (household members)', '2. Residence');

  f.input(p(14, 1), 'guardianshipLettersDate', '3. Date Letters of Guardianship of the infant were issued to petitioner', '3. Guardianship of infant', 'date',
    'Leave paragraph 3 blank if no infant child of the decedent is involved.');
  f.person('infantChild', 'Infant son or daughter of the decedent (under guardianship, under disability)', '3. Guardianship of infant',
    'Select the infant child. The name is printed in paragraphs 3, 16 and 22 (disability). Edit their details in Participants.');
  f.personField(p(15, 1), 'infantChild', 'fullName', '3. Infant son/daughter of the decedent (name)');
  f.input(p(16, 1), 'guardianshipCourtCounty', "3. Guardianship letters issued by the Surrogate's Court, ___ County", '3. Guardianship of infant', 'text',
    "The county of the Surrogate's Court that issued the guardianship letters (may differ from this court).");

  f.shared(p(17, 1), 'estate.dateOfLetters', '4. Date Limited Letters of Administration were issued');
  f.shared(p(18, 1), 'decedent.fullName', '4. Letters of Administration of the goods, chattels and credits which were of (decedent)');
  f.shared(p(19, 1), 'estate.surrogateCourtCounty', "4. Letters issued by the Surrogate's Court of ___ County");
  suffix(20, 1, '4. No bond was required of your administrat___');

  // Page 2: paragraphs 5 to 8
  const s5 = '5. Employment';
  f.input(p(21, 2), 'decedentOccupation', '5. The decedent at the time of death was employed as a (occupation)', s5);
  f.input(p(22, 2), 'decedentEmployer', '5. Employed by (employer)', s5);
  f.notApplicable(p(23, 2), '5. Employer: continuation line', 'Continuation line for a long employer name. The employer prints on the first line.');
  f.input(p(24, 2), 'decedentEmployerLocation', '5. Employer at (place of employment)', s5);
  f.input(p(25, 2), 'decedentWeeklyEarnings', '5. Earning approximately $ per week', s5, 'number');

  f.shared(p(26, 2), 'decedent.ageAtDeath', '6. Decedent age at death (years), computed from date of birth and date of death');
  f.shared(p(27, 2), 'decedent.dob', '6. Decedent born on');

  const s7 = '7. Fatal incident';
  f.input(p(28, 2), 'injuryDate', '7. Injuries were sustained on (date)', s7, 'date');
  f.input(p(29, 2), 'injuryTime', '7. Injuries were sustained at (time)', s7);
  f.input(p(30, 2), 'injuryLocation', '7. Injuries were sustained at [location]', s7);
  [31, 32, 33, 34, 35, 36, 37].forEach((n, i) =>
    f.input(p(n, 2), `fatalIncidentLine${i + 1}`, `7. Describe fatal incident: line ${i + 1}`, s7));

  const s8 = '8. Hospitalization and death';
  f.input(p(38, 2), 'hospitalName', '8. The decedent was taken to ___ Hospital', s8);
  f.shared(p(39, 2), 'decedent.dateOfDeath', '8. Where he/she died on (date of death)');
  f.input(p(40, 2), 'timeOfDeath', '8. At or about ___ a.m./p.m. (time of death, include a.m. or p.m.)', s8);
  [41, 42, 43, 44, 45, 46, 47].forEach((n, i) =>
    f.input(p(n, 2), `deathCircumstancesLine${i + 1}`, `8. Circumstances resulting in death (length of hospitalization, etc.): line ${i + 1}`, s8));

  // Page 3: paragraphs 9 to 13
  const s9 = '9. Action and settlement offer';
  f.question('defendantName', 'Defendant in the wrongful death action', s9, 'text', 'Printed wherever the form names the defendant.');
  f.filingTemplate(p(48, 3), '{{probateFiling.defendantName}}, {{estate.pendingActionCourtIndex}}',
    '9. Action commenced against the defendant (defendant; court and index number)');
  f.input(p(49, 3), 'insuranceCompany', "9. Defendant's insurance company (name, printed before \"Insurance Company\")", s9);
  f.input(p(50, 3), 'settlementAmount', '9. Final settlement offer: sum of $', s9, 'number',
    'The gross settlement. Also printed in paragraph 14 and in the order to show cause.');
  f.input(p(51, 3), 'maxInsuranceCoverage', '9. Out of maximum insurance coverage of $', s9, 'number');

  const s10 = '10. Defendant resources';
  f.answer(p(52, 3), 'defendantName', '10. Investigation of the personal resources of the defendant (name)');
  [53, 54, 55, 56].forEach((n, i) =>
    f.input(p(n, 3), `defendantAssetsLine${i + 1}`, `10. Details as to defendant's assets: line ${i + 1}`, s10));

  [57, 58, 59, 60].forEach((n, i) =>
    f.input(p(n, 3), `settlementGroundsLine${i + 1}`, `12. Grounds of belief that acceptance of the settlement is advisable: line ${i + 1}`, '12. Grounds'));

  f.people('survivors', '13. Survivors of the decedent (surviving spouse and children)', '13. Survivors', 8, 'Present age',
    'Select the survivors in the order to print. Name, relationship and date of birth come from Participants; enter each present age.');
  for (let row = 0; row < 8; row++) {
    const base = 61 + row * 4;
    f.peopleField(p(base, 3), 'survivors', row, 'fullName', `13. Survivor ${row + 1}: name`);
    f.peopleField(p(base + 1, 3), 'survivors', row, 'relationshipToDecedent', `13. Survivor ${row + 1}: relationship`);
    f.peopleField(p(base + 2, 3), 'survivors', row, 'dob', `13. Survivor ${row + 1}: date of birth`);
    f.peopleField(p(base + 3, 3), 'survivors', row, 'note', `13. Survivor ${row + 1}: present age`);
  }

  // Page 4: paragraphs 14 and 15
  const s14 = "14. Attorney's fee";
  f.input(p(93, 4), 'retainerDate', '14. Date petitioner retained the attorney', s14, 'date');
  f.shared(p(94, 4), 'lawyer.fullName', '14. Petitioner retained (attorney name), Esq.');
  f.shared(p(95, 4), 'lawyer.professionalFirmName', '14. Esq. of (law firm)');
  f.input(p(96, 4), 'attorneyDisbursements', "14. Attorney's disbursements in the sum of $", s14, 'number');
  f.answer(p(97, 4), 'settlementAmount', '14. First deducted from the gross settlement of');
  f.input(p(107, 4), 'balanceAfterDisbursements', '14. Balance of the settlement after disbursements $', s14, 'number');
  f.input(p(99, 4), 'attorneyFeeAmount', '14. A fee of $', s14, 'number');
  f.input(p(100, 4), 'attorneyFeePercent', '14. Or ___ % (fee as a percentage of the balance)', s14, 'number');
  f.input(p(101, 4), 'attorneyTotalCompensation', '14. Total compensation of $ (fee plus disbursements)', s14, 'number');

  const s15 = '15. Life expectancy and dependency';
  f.shared(p(102, 4), 'decedent.ageAtDeath', '15. At the time of death decedent was ___ years of age (computed)');
  f.shared(p(103, 4), 'decedent.dob', '15. Decedent born on');
  f.input(p(104, 4), 'decedentLifeExpectancy', '15. Decedent life expectancy (years, per vital statistics table)', s15, 'number');
  f.shared(p(105, 4), 'survivingSpouse.dob', '15. Petitioner as husband/wife and widower/widow was born on');
  f.input(p(106, 4), 'spouseLifeExpectancy', '15. Petitioner (surviving spouse) life expectancy (years)', s15, 'number');
  f.people('dependents', '15. Years of dependency: survivors sharing in the recovery (surviving spouse first, then children)', s15, 6,
    'Age on date of death',
    'List the surviving spouse first, then the children. The first three rows are also printed as the proposed distribution in the order to show cause.');
  for (let row = 0; row < 6; row++) {
    const base = 108 + row * 4;
    f.peopleField(p(base, 4), 'dependents', row, 'fullName', `15. Dependent ${row + 1}: name`);
    f.peopleField(p(base + 1, 4), 'dependents', row, 'note', `15. Dependent ${row + 1}: age on date of death`);
    f.input(p(base + 2, 4), `dependent${row + 1}Years`, `15. Dependent ${row + 1}: anticipated years of dependency`, s15, 'number');
    f.input(p(base + 3, 4), `dependent${row + 1}Percent`, `15. Dependent ${row + 1}: percentage of net amount of settlement`, s15, 'number');
  }

  // Page 5: paragraphs 16 to 18
  f.personField(p(132, 5), 'infantChild', 'fullName', '16. Except for the infant (name)');
  suffix(133, 5, '17. Petitioner as administrat___ waives commissions and bond');

  const s18 = '18. Funeral bill and claims';
  f.input(p(134, 5), 'funeralBillAmount', "18. Decedent's funeral bill in the sum of $", s18, 'number');
  f.input(p(135, 5), 'funeralBillPaidBy', '18. Funeral bill has been paid by', s18);
  suffix(136, 5, '18. Liens filed with petitioner as administrat___');
  f.input(p(137, 5), 'socialServicesClaimAmount', '18(a). Commissioner of Social Services claim of $', s18, 'number');
  f.input(p(138, 5), 'socialServicesClaimYears', '18(a). Public assistance rendered for the years', s18);
  f.input(p(139, 5), 'otherClaimantName', '18(b). Other claimant who has submitted a claim (name)', s18);
  f.input(p(140, 5), 'otherClaimAmount', '18(b). Has submitted a claim for $', s18, 'number');
  f.input(p(141, 5), 'otherClaimBasisLine1', '18(b). Claim based on: line 1', s18);
  f.input(p(142, 5), 'otherClaimBasisLine2', '18(b). Claim based on: line 2', s18);
  [143, 144, 145, 146].forEach((n, i) =>
    f.input(p(n, 5), `otherCreditorsLine${i + 1}`, `18. Other creditors, if any: line ${i + 1}`, s18));

  // Page 6: paragraphs 18(c) to 22
  f.person('claimingParent', "18(c). Decedent's father or mother who seeks a share of the recovery (nondistributee)", '18(c). Parent claim',
    'Select the parent only if a parent claims a share. Printed in paragraph 18(c) and in the order to show cause.');
  f.personField(p(147, 6), 'claimingParent', 'fullName', "18(c). Decedent's father/mother (name)");
  f.input(p(148, 6), 'publicAssistanceYears', '19. During the years ___ through ___ the decedent received Aid to Dependent Children (if applicable)', '19. Public assistance');
  f.answer(p(149, 6), 'insuranceCompany', '21. Compromise and settle with ___ Insurance Company');
  f.answer(p(150, 6), 'defendantName', '21. The claim against (defendant)');
  suffix(151, 6, '21. Settle the account of the Administrat___');

  const s22 = '22. Persons entitled to notice';
  f.person('noticeHusband', '22. Husband-Administrator (select the surviving husband if he is the petitioner)', s22,
    'Fill only the row matching the petitioner: select the husband here or the wife below.');
  f.personField(p(152, 6), 'noticeHusband', 'fullName', '22. Husband-Administrator: name');
  f.personField(p(153, 6), 'noticeHusband', 'fullAddress', '22. Husband-Administrator: address');
  f.person('noticeWife', '22. Wife-Administratrix (select the surviving wife if she is the petitioner)', s22);
  f.personField(p(154, 6), 'noticeWife', 'fullName', '22. Wife-Administratrix: name');
  f.personField(p(155, 6), 'noticeWife', 'fullAddress', '22. Wife-Administratrix: address');
  f.person('noticeDaughter', '22. Daughter', s22);
  f.personField(p(156, 6), 'noticeDaughter', 'fullName', '22. Daughter: name');
  f.personField(p(157, 6), 'noticeDaughter', 'fullAddress', '22. Daughter: address');
  f.person('noticeSon', '22. Son', s22);
  f.personField(p(158, 6), 'noticeSon', 'fullName', '22. Son: name');
  f.personField(p(159, 6), 'noticeSon', 'fullAddress', '22. Son: address');
  f.shared(p(160, 6), 'father.fullName', '22. Father: name');
  f.shared(p(161, 6), 'father.fullAddress', '22. Father: address');
  f.shared(p(162, 6), 'mother.fullName', '22. Mother: name');
  f.shared(p(163, 6), 'mother.fullAddress', '22. Mother: address');
  f.answer(p(164, 6), 'otherClaimantName', '22. Alleged Creditor: name');
  f.input(p(165, 6), 'otherClaimantAddress', '22. Alleged Creditor: address', s22);
  f.input(p(166, 6), 'taxCommissionAddress', '22. NYS Tax Comm. (Possible Creditor): address', s22);
  f.input(p(167, 6), 'socialServicesAddress', '22. Dept. Social Services (Possible Creditor): address', s22);

  // Page 7
  f.answer(p(168, 7), 'defendantName', '22. Defendant: name');
  f.input(p(169, 7), 'defendantAddress', '22. Defendant: address', s22);
  f.input(p(170, 7), 'insuranceCompanyAddress', "22. Defendant's Ins. Co.: address", s22);
  f.personField(p(171, 7), 'infantChild', 'fullName', '22. None of the above are under a disability except (infant under fourteen)');

  f.people('citationRespondents', 'Wherefore: persons to whom the citation is directed', 'Citation', 5, undefined,
    'Select the distributees and, if applicable, the Department of Social Services, NYS Tax Commission, defendant and insurer (each must exist in Participants).');
  for (let row = 0; row < 5; row++) {
    f.peopleField(p(172 + row * 2, 7), 'citationRespondents', row, 'fullName', `Citation ${row + 1}: name`);
    f.peopleField(p(173 + row * 2, 7), 'citationRespondents', row, 'fullAddress', `Citation ${row + 1}: address`);
  }
  [182, 183, 184].forEach((n, i) =>
    f.input(p(n, 7), `showCauseLine${i + 1}`, `Citation: requiring them to show cause as follows (include as applicable): line ${i + 1}`, 'Citation'));

  suffix(185, 7, 'WHY the administrat___ should not be authorized to compromise');
  f.answer(p(186, 7), 'defendantName', 'WHY: claim for wrongful death against (defendant)');
  f.answer(p(187, 7), 'settlementAmount', 'WHY: compromise for the sum of $');
  f.answer(p(188, 7), 'settlementAmount', 'WHY the entire recovery of $ should not be allocated to wrongful death');
  f.shared(p(189, 7), 'estate.dateOfLetters', 'WHY: Letters of Administration heretofore issued on (date)');
  suffix(190, 7, 'WHY: restraining the administrat___');

  // Page 8
  f.shared(p(191, 8), 'executor.fullName', 'WHY the account of (petitioner)');
  suffix(192, 8, 'WHY the account of ___ as Administrat___');
  f.answer(p(193, 8), 'defendantName', 'WHY defendant (name) or insurer should not pay');
  f.shared(p(194, 8), 'lawyer.professionalFirmName', 'WHY: pay to the firm of ___ Esqs.');
  f.answer(p(195, 8), 'attorneyFeeAmount', "WHY: sum of $ as and for attorney's fees");
  f.answer(p(196, 8), 'attorneyDisbursements', 'WHY: together with disbursements of $');
  f.notApplicable(p(197, 8), 'WHY: disbursements continuation line', 'Continuation of the disbursements amount, which prints in full on the line above.');
  f.input(p(198, 8), 'netBalanceForDistribution', 'WHY: balance of the settlement to be distributed, the sum of $ (after fee and disbursements)', s14, 'number');
  f.answer(p(199, 8), 'dependent1Percent', 'WHY: ___% of the balance to the widow/widower (dependent 1 percentage)');
  f.peopleField(p(200, 8), 'dependents', 0, 'fullName', 'WHY: to (widow/widower, dependent 1 name)');
  f.notApplicable(p(201, 8), 'WHY: widow/widower name continuation line', 'Continuation line for a long name. The name prints on the line above.');
  f.answer(p(202, 8), 'dependent2Percent', 'WHY: ___% of the balance to (child, dependent 2 percentage)');
  f.peopleField(p(203, 8), 'dependents', 1, 'fullName', 'WHY: child of decedent (dependent 2 name)');
  f.answer(p(204, 8), 'dependent3Percent', 'WHY: ___% of the balance to (child, dependent 3 percentage)');
  f.peopleField(p(205, 8), 'dependents', 2, 'fullName', 'WHY: child of decedent (dependent 3 name)');
  f.personField(p(206, 8), 'claimingParent', 'fullName', 'WHY the claim of (parent) should not be rejected as a nondistributee');
  f.answer(p(207, 8), 'otherClaimantName', 'WHY the claim of (other claimant)');
  f.answer(p(208, 8), 'otherClaimAmount', 'WHY: in the amount of $ should not be rejected');
  f.answer(p(209, 8), 'defendantName', 'WHY: upon payments by the said defendant');
  f.answer(p(210, 8), 'insuranceCompany', 'WHY: the ___ Insurance Company');
  suffix(211, 8, 'WHY: the petitioner, as administrat___');
  f.shared(p(212, 8), 'decedent.fullName', 'WHY: goods, chattels and credits that were of (decedent), deceased');
  f.answer(p(213, 8), 'defendantName', 'WHY: execute and deliver to the said defendant a release');

  // Page 9: signature, verification, attorney
  f.signature(p(214, 9), 'Date signed (completed at signing)');
  f.signature(p(215, 9), 'Petitioner signature');
  f.notary(p(216, 9), 'Verification: County of (where sworn)');
  f.shared(p(217, 9), 'executor.fullName', 'Verification: deponent (petitioner) name, being duly sworn');
  f.signature(p(218, 9), 'Verification: petitioner signature');
  f.notary(p(219, 9), 'Jurat: sworn to before me this ___ day');
  f.notary(p(220, 9), 'Jurat: day of (month)');
  f.notary(p(221, 9), 'Jurat: year');
  f.notary(p(222, 9), 'Notary Public signature');
  f.signature(p(223, 9), 'Signature of Attorney');
  f.shared(p(224, 9), 'lawyer.fullName', 'Attorney: print name');
  f.shared(p(225, 9), 'lawyer.professionalFirmName', 'Attorney: firm name');
  f.shared(p(226, 9), 'lawyer.phone', 'Attorney: tel. no.');
  f.shared(p(227, 9), 'lawyer.fullAddress', 'Attorney: address');
});
